// src/components/OfficeHoursFields.jsx
// A client's office hours (3.61.0, owner): working days, opening and closing
// times and time zone. Our automatic jobs for the client (automatic retries,
// the slow lane for hard-to-reach numbers) only send
// inside them; faxes people send go out at once. Used in Settings → Office
// Hours (Admins) and Console → Clients (Super Admins). Backend:
// lib/businessHours.js.
export const DEFAULT_HOURS = { tz: 'America/New_York', days: [1, 2, 3, 4, 5], open: '08:00', close: '17:00' };
const DAYS = [[1, 'Mon'], [2, 'Tue'], [3, 'Wed'], [4, 'Thu'], [5, 'Fri'], [6, 'Sat'], [0, 'Sun']];
// The usual ones first, then every zone the browser knows
const COMMON = [
  ['America/New_York', 'Eastern (New York)'], ['America/Chicago', 'Central (Chicago)'], ['America/Denver', 'Mountain (Denver)'],
  ['America/Phoenix', 'Arizona (Phoenix)'], ['America/Los_Angeles', 'Pacific (Los Angeles)'], ['America/Anchorage', 'Alaska'], ['Pacific/Honolulu', 'Hawaii'],
  ['America/Toronto', 'Toronto'], ['America/Vancouver', 'Vancouver'], ['Asia/Kolkata', 'India'], ['Europe/London', 'United Kingdom'],
];
const ALL = (() => { try { return Intl.supportedValuesOf('timeZone'); } catch { return []; } })();
const mins = s => { const m = /^(\d{2}):(\d{2})$/.exec(s || ''); return m ? +m[1] * 60 + +m[2] : null; };

// "Mon–Fri, 8:00 AM – 5:00 PM ET" (as lib/businessHours.js label)
export function hoursText(h) {
  h = h || DEFAULT_HOURS;
  const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const t = s => { const m = mins(s); const hh = Math.floor(m / 60), mm = m % 60; return `${hh % 12 || 12}:${String(mm).padStart(2, '0')} ${hh < 12 ? 'AM' : 'PM'}`; };
  const ds = [...h.days].sort();
  const run = ds.length > 2 && ds.every((x, i) => i === 0 || x === ds[i - 1] + 1);
  let zone = h.tz;
  try { zone = new Intl.DateTimeFormat('en-US', { timeZone: h.tz, timeZoneName: 'shortGeneric' }).formatToParts(new Date()).find(x => x.type === 'timeZoneName')?.value || h.tz; } catch { /* keep the name */ }
  return `${run ? `${names[ds[0]]}–${names[ds[ds.length - 1]]}` : ds.map(x => names[x]).join(', ')}, ${t(h.open)} – ${t(h.close)}${mins(h.close) < mins(h.open) ? ' (next day)' : ''} ${zone}`;
}

// The problem with these hours, or '' when they're fine (as the backend checks)
export function hoursProblem(h) {
  if (!h?.tz) return 'Pick a time zone.';
  if (!h.days?.length) return 'Pick at least one working day.';
  const o = mins(h.open), c = mins(h.close);
  if (o == null || c == null) return 'Give the opening and closing times.';
  // 3.61.0 (owner): a closing time before the opening time is an overnight shift (it ends the next day)
  if (((c - o + 1440) % 1440) < 60) return 'Office hours must be at least an hour long.';
  return '';
}

export default function OfficeHoursFields({ value, onChange, disabled }) {
  const h = value || DEFAULT_HOURS;
  const set = (k, v) => onChange({ ...h, [k]: v });
  const problem = hoursProblem(h);
  return (
    <div>
      <div style={S.row} role="group" aria-label="Working days">
        {DAYS.map(([d, l]) => {
          const on = h.days.includes(d);
          return (
            <label key={d} style={{ ...S.day, ...(on ? S.dayOn : {}) }}>
              <input type="checkbox" checked={on} disabled={disabled} style={S.hidden}
                onChange={e => set('days', e.target.checked ? [...h.days, d].sort() : h.days.filter(x => x !== d))} />{l}
            </label>
          );
        })}
      </div>
      <div style={{ ...S.row, marginTop: 10 }}>
        <label style={S.field}><span style={S.label}>Opens</span>
          <input type="time" value={h.open} disabled={disabled} onChange={e => set('open', e.target.value)} style={S.input} /></label>
        <label style={S.field}><span style={S.label}>Closes</span>
          <input type="time" value={h.close} disabled={disabled} onChange={e => set('close', e.target.value)} style={S.input} /></label>
        <label style={{ ...S.field, minWidth: 220 }}><span style={S.label}>Time zone</span>
          <select value={h.tz} disabled={disabled} onChange={e => set('tz', e.target.value)} style={S.input}>
            {COMMON.map(([z, l]) => <option key={z} value={z}>{l}</option>)}
            <option disabled>──────────</option>
            {ALL.filter(z => !COMMON.some(([c]) => c === z)).map(z => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
          </select></label>
      </div>
      {!problem && mins(h.close) < mins(h.open) && <div style={S.note}>Overnight: each shift starts on the days ticked and ends the next morning.</div>}
      {problem && <div style={S.problem}>{problem}</div>}
    </div>
  );
}

const S = {
  row: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' },
  day: { padding: '6px 12px', borderRadius: 999, border: '1.5px solid var(--c-cfd8dc)', fontSize: 13, fontWeight: 600, color: 'var(--t-546e7a)', cursor: 'pointer', userSelect: 'none', background: 'var(--c-ffffff)' },
  dayOn: { background: 'var(--c-e0f2f1)', borderColor: 'var(--c-00897b)', color: 'var(--t-00695c)' },
  hidden: { position: 'absolute', opacity: 0, width: 1, height: 1, pointerEvents: 'none' },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: 12, fontWeight: 700, color: 'var(--t-546e7a)' },
  input: { padding: '7px 10px', borderRadius: 8, border: '1.5px solid var(--c-cfd8dc)', fontSize: 14, fontFamily: 'inherit', background: 'var(--c-ffffff)', color: 'var(--t-37474f)' },
  problem: { marginTop: 8, fontSize: 13, color: 'var(--t-c62828)' },
  note: { marginTop: 8, fontSize: 12.5, color: 'var(--t-607d8b)' },
};
